import { getFont } from "@flatfile/design-system";
import { Border } from "../../common/UI/Borders.js";
import { FlexCenter, Flex } from "../../common/UI/Flex.js";
import { Shadows } from "../../common/UI/Shadows.js";
import { ZIndex } from "../../common/UI/ZIndex.js";
import { DragHandle } from "../Cell/Index/DragHandleStyles.js";
import { HIDE_TOOLTIPS_CLASS, CELL_TOOLTIP_CLASS, TABLE_HIDDEN_ROWS_AT_LIMIT } from "./constants.js";
import styled, { css, createGlobalStyle } from "styled-components";
import { ColumnOverlayWrapper } from "../Cell/Index/ColumnOverlayStyles.js";
const TableSkeleton = ({
  theme,
  height,
  width
}) => css`
  &:before {
    content: '';
    position: absolute;
    left: ${theme.indexColumn.width}px;
    top: -1px;
    bottom: 0;
    height: ${height}px;
    width: ${width - theme.indexColumn.width}px;
    background: conic-gradient(
        from 90deg at 1px 1px,
        ${theme.backgroundColor} 90deg,
        ${theme.cell.borderColor} 0
      )
      0 0 / ${theme.column.width}px ${theme.rowHeight}px;
  }
`;
const RowSkeleton = ({
  theme,
  width
}) => css`
  &:before {
    position: absolute;
    left: ${theme.indexColumn.width}px;
    top: -1px;
    bottom: 0;
    content: '';
    width: ${width - theme.indexColumn.width}px;

    background: conic-gradient(
        from 90deg at 1px 1px,
        ${theme.indexColumn.backgroundColor} 90deg,
        ${theme.cell.borderColor} 0
      )
      0 0 / ${theme.column.width}px ${theme.rowHeight}px;
  }
}
`;
const ColumnSkeleton = ({
  theme,
  left,
  width,
  backgroundColor
}) => css`
  &:before {
    position: absolute;
    left: ${left}px;
    top: -1px;
    bottom: 0;
    content: '';
    width: ${width - 1}px;

    background: conic-gradient(
        from 90deg at 1px 1px,
        transparent 90deg,
        ${theme.cell.borderColor} 0
      )
      0 0 / ${width}px ${theme.rowHeight}px;

    background-color: ${backgroundColor};
    border-bottom: ${Border.small({ color: backgroundColor })};
    box-sizing: border-box;
  }
}
`;
const OuterTableElement = styled.div`
  ${({ theme }) => css`
    border-top: ${Border.small({ color: theme.table.borderColor })};
    background-color: ${theme.table.backgroundColor};
  `}
`;
const TableToolsContainer = styled.div`
  ${({ theme }) => css`
    position: sticky;
    width: auto;
    height: 0px;
    top: 0px;
    left: 0px;
    z-index: ${ZIndex.level1000};
    > div {
      position: absolute;
      top: 0px;
      right: 0px;
      width: 32px;
      height: 32px;
      border-width: 0 1px 1px 1px;
      border-color: ${theme.cell.borderColor};
      background: ${theme.table.backgroundColor};
    }
  `}
`;
styled(OuterTableElement)`
  ${FlexCenter}
  height: 100%;
`;
const HeaderRowElement = styled.div`
  width: 100%;
  height: ${(props) => props.height}px;
  background: ${(props) => props.theme.indexColumn.backgroundColor};
  position: sticky;
  z-index: ${ZIndex.level600};
  top: 0;

  ${(props) => props.showSkeleton && RowSkeleton(props)}
`;
const PinnedColumnsWrapper = styled.div`
  ${Flex}
  ${({ offset }) => `margin-top: -${offset}px;`}
  ${({ width }) => `width: ${width}px;`}
  ${({ hasPinnedColumns }) => hasPinnedColumns && `box-shadow: ${Shadows.pinnedColumn};`}
  height: 100%;
  background-color: ${({ theme }) => theme.table.backgroundColor};
  position: sticky;
  left: 0;
  z-index: ${ZIndex.level600};
`;
const AddNewRecordStickyFooterWrapper = styled.div`
  position: sticky;
  bottom: ${({ bottom }) => bottom ? `${bottom}px` : "0"};
  width: ${({ isAdding, width }) => !isAdding ? "fit-content" : `${width}px`};
  min-width: fit-content;
  border-top: ${({ theme }) => Border.small({ color: theme.cell.borderColor })};
  background: ${({ theme }) => theme.backgroundColor};
  z-index: ${ZIndex.level700};
  left: 0;
  ${({ isAdding }) => isAdding ? "filter: drop-shadow(0px 0px 3px var(--colors-tt-cell-border));" : ""}
`;
styled.div`
  margin-left: ${({ marginLeft }) => marginLeft}px;
`;
const StickyFooterWrapper = styled.div`
  ${Flex};
  position: sticky;
  bottom: 0;
  background-color: var(--colors-tt-cell-border);
  z-index: ${ZIndex.level700};
`;
const PinnedColumnElement = styled.div`
  flex-shrink: 0;

  ${({ index, theme, left, width }) => {
  if (index) {
    return css`
        background: ${theme.indexColumn.backgroundColor};
        width: ${theme.indexColumn.width}px;
        border-right: ${Border.small({ color: theme.indexColumn.borderColor })};
        ${ColumnSkeleton({
      theme,
      left: -1,
      width: theme.indexColumn.width + 2,
      backgroundColor: theme.indexColumn.backgroundColor
    })}
      `;
  }
  return ColumnSkeleton({
    theme,
    left,
    width: width + 1,
    backgroundColor: theme.backgroundColor
  });
}}
`;
const TooltipStyles = css`
  font-size: ${({ theme }) => theme.tooltip.fontSize};
  z-index: ${ZIndex.level1000};
`;
const InnerTable = styled.div`
  position: relative;

  .tooltip {
    ${TooltipStyles}
    position: absolute;
  }

  .tooltip.place-right:after {
    top: 14px;
  }

  .tooltip .tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 4px;

    mono {
      ${getFont("b2Mono")}
      font-size: ${({ theme }) => `calc(${theme.tooltip.fontSize} - 1px)`};
    }
  }

  &.${HIDE_TOOLTIPS_CLASS} .${CELL_TOOLTIP_CLASS} {
    display: none;
  }

  ${(props) => props.showSkeleton && TableSkeleton(props)}
`;
const MaximumSizeMessage = styled.div`
  position: absolute;
  z-index: ${ZIndex.level900};
  bottom: -${({ theme }) => theme.rowHeight}px;
  opacity: 90%;
  background-color: ${({ theme }) => theme.backgroundColor};
  width: 100%;
  height: ${({ theme }) => theme.rowHeight * TABLE_HIDDEN_ROWS_AT_LIMIT}px;
  text-align: center;
  display: flex;
  align-items: center;
`;
const MaximumMessageInner = styled.div`
  position: sticky;
  left: 0;
  width: ${({ $width }) => $width + "px"};
  padding-right: 24px;
  padding-left: 24px;
`;
const iconStrokeWidth = (size) => `
  path {
    stroke: currentColor;
    stroke-width: ${size ?? 0.5}px;
  }
`;
const buttonStyles = ({
  icon,
  active
}) => css`
  height: 24px;

  &:hover {
    background-color: ${({ theme }) => theme.buttons.backgroundColor.hover};
  }

  &:active {
    background-color: ${({ theme }) => theme.buttons.backgroundColor.active};
  }

  ${icon ? css`
        height: 20px;
        width: 20px;
        padding: 0px;
        ${iconStrokeWidth()}

        svg {
          color: ${({ theme }) => active ? theme.cell.active.borderColor : theme.buttons.iconColor} !important;
        }
      ` : css`
        border-radius: ${({ theme }) => theme.buttons.pill.borderRadius};
        background-color: ${({ theme }) => theme.buttons.pill.backgroundColor} !important;
        color: ${({ theme }) => theme.buttons.pill.color} !important;
        div,
        svg {
          color: ${({ theme }) => theme.buttons.pill.color} !important;
        }
      `}
`;
const TableHeightRootStyle = createGlobalStyle`
  ${DragHandle} {
    --table-height: ${(props) => props.height && props.height}px;
  }
  ${ColumnOverlayWrapper} {
    height: ${(props) => (props == null ? void 0 : props.height) && props.height}px;
  }
`;
const AddNewRecordPinnedCellWrapper = styled.div`
  flex-shrink: 0;
  background: ${({ theme }) => theme.indexColumn.backgroundColor};
  width: ${({ width }) => width}px;
  border-right: ${({ theme }) => Border.small({ color: theme.indexColumn.borderColor })};
  display: flex;
  height: ${({ theme }) => theme.rowHeight}px;
  position: sticky;
  left: 0;
  z-index: ${ZIndex.level700};
  cursor: pointer;
`;
const AddNewRecordButton = styled.div`
  background: ${({ theme }) => theme.indexColumn.backgroundColor};
  width: ${({ width }) => width}px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: sticky;
  position: sticky;
  left: 0;
  z-index: ${ZIndex.level700};
`;
styled.div`
  flex-shrink: 0;
  background: ${({ theme }) => theme.indexColumn.backgroundColor};
  width: ${({ width }) => width}px;
  border-right: ${({ theme }) => Border.small({ color: theme.indexColumn.borderColor })};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => theme.rowHeight}px;
  position: sticky;
  left: 0;
  z-index: ${ZIndex.level700};
  cursor: pointer;
`;
export {
  AddNewRecordButton,
  AddNewRecordPinnedCellWrapper,
  AddNewRecordStickyFooterWrapper,
  HeaderRowElement,
  InnerTable,
  MaximumMessageInner,
  MaximumSizeMessage,
  OuterTableElement,
  PinnedColumnElement,
  PinnedColumnsWrapper,
  StickyFooterWrapper,
  TableHeightRootStyle,
  TableToolsContainer,
  TooltipStyles,
  buttonStyles,
  iconStrokeWidth
};
